<template>
  <div class="w-full rounded-md bg-white">
    <div class="mb-3 flex items-center justify-between">
      <h2 class="text-base font-medium">Agrupa tus productos</h2>
      <button @click="emit('update:modelValue', false)">
        <XMarkIcon class="size-5 text-gray-400" />
      </button>
    </div>
    <form @submit="onSubmit">
      <div class="mb-4">
        <label class="mb-1 block text-sm font-medium">Buscar y agregar productos</label>
        <div class="relative">
          <SearchBar
            v-model="searchText"
            name="product-search"
            placeholder="Buscar productos..."
            size="sm"
            class="min-h-10"
            :debounce-time="300"
            @search-focus="() => (isSearchFocused = true)"
          />
          <div
            v-if="isSearchFocused"
            class="mt-1 flex max-h-60 w-full flex-col space-y-1 overflow-auto rounded-md border bg-white shadow-lg"
            @focusout="(isSearchFocused = false)"
          >
            <div class="flex w-full items-center justify-between p-2">
              <div class="text-xs font-medium text-gray-600">
                {{ searchResults.length }} producto{{ searchResults.length !== 1 ? 's' : '' }}
              </div>
            </div>
            <button
              v-for="product in searchResults"
              :key="product.id"
              class="flex w-auto flex-col items-center justify-between rounded p-2 hover:bg-gray-50"
              @click.prevent="addProduct(product)"
            >
              <div class="flex w-full justify-between">
                <div class="flex items-center space-x-2">
                  <img
                    :src="product.images?.[0]?.derivatives?.jpgSm?.url"
                    alt=""
                    class="size-8 shrink-0 rounded object-cover"
                  />
                  <div class="flex flex-col items-start">
                    <div class="line-clamp-1 text-xs font-medium text-gray-900">{{ product.name }}</div>
                    <div class="text-xs text-gray-500">{{ product.sku }}</div>
                  </div>
                </div>
                <button
                  type="button"
                  class="shrink-0 rounded border border-lk-green bg-white px-2 py-1 text-xs text-lk-green hover:bg-lk-green hover:text-white disabled:border-gray-300 disabled:bg-gray-300 disabled:text-gray-500"
                  :disabled="isProductSelected(product.id)"
                  @click="addProduct(product)"
                >
                  {{ isProductSelected(product.id) ? 'Agregado' : 'Agregar' }}
                </button>
              </div>
            </button>
            <div
              v-if="hasNextPage && !isSearchLoading"
              class="mt-2 text-center"
            >
              <button
                type="button"
                class="text-xs text-lk-green hover:underline"
                @click="loadMoreProducts"
              >
                Cargar más productos
              </button>
            </div>
            <div
              v-if="isSearchLoading"
              class="py-2 text-center text-xs text-gray-500"
            >
              Cargando...
            </div>
          </div>
        </div>
      </div>
      <div class="mb-4">
        <label class="mb-1 block text-sm font-medium">Productos seleccionados</label>
        <div
          v-if="selectedProducts.length === 0"
          class="rounded border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500"
        >
          No hay productos seleccionados. Busca y agrega productos arriba.
        </div>
        <div
          v-else
          class="rounded border border-gray-200 p-3"
        >
          <div class="mb-1 text-xs font-medium text-gray-600">
            {{ selectedProducts.length }} producto{{ selectedProducts.length !== 1 ? 's' : '' }} seleccionado{{
              selectedProducts.length !== 1 ? 's' : ''
            }}
          </div>
          <div class="grid grid-cols-2 gap-1.5 sm:grid-cols-3 md:grid-cols-4">
            <div
              v-for="product in selectedProducts"
              :key="product.id"
              class="flex rounded bg-white p-1 shadow-sm"
            >
              <div class="flex w-full items-center justify-between space-x-1">
                <img
                  :src="product.images?.[0]?.derivatives?.jpgSm?.url"
                  alt=""
                  class="size-10 shrink-0 rounded object-cover"
                />
                <div class="text-left">
                  <div class="line-clamp-1 text-xs font-medium text-gray-900">{{ product.name }}</div>
                  <div class="text-xs text-gray-500">{{ product.sku }}</div>
                </div>
                <button
                  type="button"
                  class="ml-auto rounded-full bg-red-100 p-0.5 text-red-600 hover:bg-red-200"
                  @click="removeProduct(product)"
                >
                  <XMarkIcon class="size-2.5" />
                </button>
                <!-- <button
                  type="button"
                  class="text-xs text-gray-500 hover:underline"
                  @click="removeProduct(product)"
                >
                  Eliminar
                </button> -->
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mb-3">
        <label class="mb-1 block text-sm font-medium">Producto Padre</label>
        <LokalSelector
          v-model="selectedParent"
          :options="parentOptions"
          options-label="Seleccionar producto padre"
          option-label="name"
          key-option="id"
          :multiple="false"
          :disabled="selectedProducts.length < 2"
        />
        <div
          v-if="selectedProducts.length < 2"
          class="mt-0.5 text-xs text-gray-500"
        >
          Requiere 2+ productos
        </div>
      </div>
      <div class="mb-4 grid grid-cols-3 gap-3">
        <div>
          <label class="mb-0.5 block text-xs font-medium">Atributo 1</label>
          <LokalSelector
            v-model="selectedAttribute1"
            :options="attributeOptions"
            options-label="Tipo"
            option-label="label"
            key-option="value"
            :multiple="false"
          />
        </div>
        <div>
          <label class="mb-0.5 block text-xs font-medium"
            >Atributo 2 <span class="hidden sm:inline">(Opcional)</span></label
          >
          <LokalSelector
            v-model="selectedAttribute2"
            :options="attributeOptions"
            options-label="Tipo"
            option-label="label"
            key-option="value"
            :multiple="false"
          />
        </div>
        <div>
          <label class="mb-0.5 block text-xs font-medium"
            >Atributo 3 <span class="hidden sm:inline">(Opcional)</span></label
          >
          <LokalSelector
            v-model="selectedAttribute3"
            :options="attributeOptions"
            options-label="Tipo"
            option-label="label"
            key-option="value"
            :multiple="false"
          />
        </div>
      </div>
      <div class="flex justify-end space-x-2">
        <button
          type="button"
          class="rounded border px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          @click="emit('update:modelValue', false)"
        >
          Cancelar
        </button>
        <button
          type="submit"
          class="rounded bg-lk-green px-3 py-1.5 text-sm text-white hover:bg-lk-green-deep disabled:bg-gray-400"
          :disabled="!canCreate || isSubmitting"
        >
          {{ isSubmitting ? 'Creando...' : `Crear (${selectedProducts.length})` }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { useForm } from 'vee-validate';
import { useMutation, useInfiniteQuery } from 'vue-query';
import productGroupsApi from 'api/product-groups';
import productsApi from 'api/products';
import useMessageStore from 'stores/message-store';
import LokalSelector, { type OptionRecord } from './shared/lokal-selector.vue';
import SearchBar from './shared/search-bar.vue';

const props = defineProps<{
  modelValue: boolean;
  maker: Maker;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'group-created': [];
}>();

const messageStore = useMessageStore();

const { handleSubmit, isSubmitting } = useForm();

const selectedProducts = ref<Product[]>([]);
const searchText = ref('');
const selectedParent = ref<OptionRecord>({});
const selectedAttribute1 = ref<OptionRecord>({ value: 'not_set', label: 'Sin definir' });
const selectedAttribute2 = ref<OptionRecord>({ value: 'not_set', label: 'Sin definir' });
const selectedAttribute3 = ref<OptionRecord>({ value: 'not_set', label: 'Sin definir' });
const isSearchFocused = ref(false);

const parentOptions = computed(() => selectedProducts.value.map((p) => ({ id: p.id, name: `${p.name} (${p.sku})` })));

const attributeOptions = [
  { value: 'not_set', label: 'Sin definir' },
  { value: 'size', label: 'Talla' },
  { value: 'color', label: 'Color' },
  { value: 'model', label: 'Modelo' },
];

const canCreate = computed(() => selectedProducts.value.length >= 2 && selectedParent.value.id);

const searchQueryParams = computed(() => {
  const baseParams = `q[maker_id_eq]=${props.maker.id}&q[s][]=status+desc&q[s][]=maker_priority+asc`;
  if (searchText.value && searchText.value.length >= 2) {
    return `${baseParams}&search_text=${encodeURIComponent(searchText.value)}`;
  }
  return '';
});

const searchEnabled = computed(() => !!(searchText.value && searchText.value.length >= 2));

function fetchProducts({ pageParam = 1 }) {
  return productsApi.newIndex(pageParam, searchQueryParams.value);
}

const productsSearchQuery = useInfiniteQuery(['productSearch', props.maker.id, searchText], fetchProducts, {
  enabled: searchEnabled,
  getNextPageParam: (lastPage) => {
    const currentPage = parseInt(lastPage.data.headers['x-page'], 10);
    const totalPages = Math.ceil(
      parseInt(lastPage.data.headers['x-total'], 10) / parseInt(lastPage.data.headers['x-per-page'], 10),
    );
    return currentPage < totalPages ? currentPage + 1 : undefined;
  },
  staleTime: 30000,
  cacheTime: 60000,
});

const searchResults = computed(
  () =>
    productsSearchQuery.data?.value?.pages?.reduce(
      (acc, page) => [...acc, ...(page?.data?.products || [])],
      [] as Product[],
    ) || [],
);

const isSearchLoading = computed(() => productsSearchQuery.isLoading.value);
const hasNextPage = computed(() => !!productsSearchQuery.hasNextPage?.value);

function loadMoreProducts() {
  if (hasNextPage.value && !isSearchLoading.value) {
    productsSearchQuery.fetchNextPage.value();
  }
}

function isProductSelected(productId: number): boolean {
  return selectedProducts.value.some((product) => product.id === productId);
}

function addProduct(product: Product) {
  if (!isProductSelected(product.id)) {
    selectedProducts.value.push(product);
  }
}

function removeProduct(productToRemove: Product) {
  selectedProducts.value = selectedProducts.value.filter((product) => product.id !== productToRemove.id);
  if (selectedParent.value.id === productToRemove.id) {
    selectedParent.value = {};
  }
}

const createGroupMutation = useMutation((data: any) => productGroupsApi.createGroup(props.maker.id, data), {
  onSuccess: () => {
    messageStore.showMessage('Grupo creado exitosamente', 'success');
    emit('group-created');
    emit('update:modelValue', false);
    resetForm();
    window.location.reload();
  },
  onError: (error: any) => {
    messageStore.showMessage(error.response?.data?.detail || 'Error al crear grupo', 'error');
  },
});

const onSubmit = handleSubmit(() => {
  createGroupMutation.mutate({
    productIds: selectedProducts.value.map((product) => product.id),
    productGroupData: {
      parentProductId: selectedParent.value.id,
      attribute1Type: selectedAttribute1.value.value,
      attribute2Type: selectedAttribute2.value.value,
      attribute3Type: selectedAttribute3.value.value,
    },
  });
});

function resetForm() {
  selectedProducts.value = [];
  searchText.value = '';
  selectedParent.value = {};
  selectedAttribute1.value = { value: 'not_set', label: 'Sin definir' };
  selectedAttribute2.value = { value: 'not_set', label: 'Sin definir' };
  selectedAttribute3.value = { value: 'not_set', label: 'Sin definir' };
  isSearchFocused.value = false;
}

watch(selectedProducts, () => {
  if (selectedParent.value.id && !selectedProducts.value.find((product) => product.id === selectedParent.value.id)) {
    selectedParent.value = {};
  }
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) resetForm();
  },
);
</script>
